import { useNavigate } from 'react-router-dom'
import type { Party } from '@/types'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { IdeologyMini, IdeologyDisplay } from '@/components/ideology/IdeologyDisplay'
import { PartyCardSkeleton } from '@/components/party/PartyCardSkeleton'

interface NationPartiesListProps {
  parties: Party[]
  maxParties: number
  myPartyId?: string
  isLoading: boolean
  error: Error | null
}

interface PublicPartyCardProps {
  party: Party
  isUserParty: boolean
}

/**
 * Public party card showing only public information.
 * Does NOT show: money, action_points, internal candidate details
 */
function PublicPartyCard({ party, isUserParty }: PublicPartyCardProps) {
  const navigate = useNavigate()

  return (
    <Card
      className="cursor-pointer transition-shadow hover:shadow-md"
      onClick={() => navigate(`/parties/${party.id}`)}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <div
              className="h-4 w-4 shrink-0 rounded-full"
              style={{ backgroundColor: party.color }}
            />
            <CardTitle className="text-base">
              {party.name} ({party.abbreviation})
            </CardTitle>
          </div>
          {isUserParty && <Badge variant="secondary">Your Party</Badge>}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 text-sm">
          {/* Slogan */}
          {party.slogan && (
            <p className="text-muted-foreground italic line-clamp-2">"{party.slogan}"</p>
          )}

          {/* Ideology visualization */}
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-muted-foreground">
              <span className="text-xs font-medium">Ideology</span>
              <IdeologyMini ideology={party.ideology_vector} />
            </div>
            <IdeologyDisplay ideology={party.ideology_vector} showLabels={false} compact />
          </div>

          {/* Seat count placeholder - will be populated after elections */}
          <div className="flex items-center justify-between border-t pt-2 text-xs text-muted-foreground">
            <span>Seats: 0</span>
            <span>Prestige: {party.prestige}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export function NationPartiesList({
  parties,
  maxParties,
  myPartyId,
  isLoading,
  error,
}: NationPartiesListProps) {
  return (
    <div>
      <div className="mb-6 flex items-center gap-3">
        <h2 className="text-xl font-semibold">Political Parties</h2>
        <Badge variant="outline">
          {parties.length}/{maxParties}
        </Badge>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
          Failed to load parties. Please try again later.
        </div>
      )}

      {isLoading && (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <PartyCardSkeleton key={i} />
          ))}
        </div>
      )}

      {!isLoading && !error && parties.length === 0 && (
        <div className="rounded-lg border border-dashed p-12 text-center">
          <h3 className="text-lg font-medium">No parties yet</h3>
          <p className="text-muted-foreground mt-1">
            Be the first to create a political party in this nation!
          </p>
        </div>
      )}

      {!isLoading && !error && parties.length > 0 && (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {parties.map((party) => (
            <PublicPartyCard
              key={party.id}
              party={party}
              isUserParty={myPartyId === party.id}
            />
          ))}
        </div>
      )}
    </div>
  )
}
